"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Sparkles, X } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useT } from "@/lib/use-i18n";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import { useFocusTrapDialog } from "@/components/chat/hooks/use-focus-trap-dialog";
import {
  PLAN_CHANGED_NOTICE_KEY,
  PLAN_SNAPSHOT_KEY,
} from "@/lib/plan-change-keys";
import { fetchUsageShared } from "@/components/chat/hooks/use-chat-quotas";

// 2026-09-19 perf-cpu (research 2026 AWS jitter + free-tier CPU budget):
// base 16min + jitter aleatorio 0-120s por tick (ventana efectiva 16-18min,
// techo ≥15min AGENTS.md). El jitter evita thundering herd tras deploys
// (N tabs despiertan a la vez) y el setTimeout encadenado re-sortea cada
// tick en vez de un setInterval fijo.
const CHECK_BASE_MS = 16 * 60 * 1000;
const CHECK_JITTER_MS = 120_000;
function nextCheckDelayMs(): number {
  return CHECK_BASE_MS + Math.floor(Math.random() * (CHECK_JITTER_MS + 1));
}

type PlanTier = "free" | "go" | "pro" | "max" | "maxplus";

// 2026-09-23 FASE 2 PERF-01 (research 2026 SWR dedup + free-tier CPU budget —
// 1 owner + in-flight compartido, paridad fetchUsageShared): visibilitychange
// puede disparar ráfagas (ocultar/mostrar rápido) que compiten con el tick —
// sin dedup cada evento es 1 fetch a /api/auth/me. La promesa en vuelo se
// comparte; solo colapsa solapes reales, nunca suprime un re-check
// secuencial (el re-chequeo al volver visible sigue intacto).
// 2026-09-24 FASE 2 FIXER L-47 (research 2026 SWR dedupingInterval + paridad
// OnlineGuard PROBE_DEDUP_MS 30s): in-flight solo colapsa solapes reales —
// la ventana 30s por tab colapsa ticks espaciados (visibility ×3 en 40s = 1
// fetch). Force no existe aquí (todos son ticks automáticos); el refresh
// manual es el botón reload con sessionStorage, no un fetch.
let planCheckInflight: Promise<void> | null = null;
let planCheckLastMs = 0;
const PLAN_CHECK_DEDUP_MS = 30_000;

/** Test-only: resetea la ventana dedup entre tests (paridad OnlineGuard
 * __resetOnlineProbeForTests; research 2026 SWR dedup, verificado
 * websearch 2026-09-24). */
export function __resetPlanChangeDedupForTests() {
  planCheckInflight = null;
  planCheckLastMs = 0;
}

interface PlanState {
  plan: PlanTier;
}

/**
 * Normalize the plan tier from `/api/auth/me`. `null` means a signed-in free
 * user; an explicit `"free"` string comes from stored snapshots. `undefined`
 * means the endpoint could not resolve a real plan (e.g. the local E2E stub)
 * so we must not treat it as a change.
 */
function normalizePlan(value: string | null | undefined): PlanTier | null {
  // RESUELTO 2026-09-12 [B-P3] incluía maxplus (tick 15m ciego a Max+) |fuente src/lib/plans.ts:150-172
  if (
    value === "pro" ||
    value === "max" ||
    value === "go" ||
    value === "maxplus"
  )
    return value;
  if (value === null || value === "free") return "free";
  return null;
}

function readSnapshot(): PlanState | null {
  try {
    const raw = localStorage.getItem(PLAN_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PlanState>;
    if (typeof parsed?.plan !== "string") return null;
    const plan = normalizePlan(parsed.plan);
    return plan ? { plan } : null;
  } catch {
    return null;
  }
}

function writeSnapshot(plan: PlanTier) {
  try {
    localStorage.setItem(PLAN_SNAPSHOT_KEY, JSON.stringify({ plan }));
  } catch {
    // Storage may be blocked — the reload still refreshes server-rendered UI.
  }
}

/**
 * Detects plan changes made from the admin panel (or backend expiry) and asks
 * the user to refresh, mirroring the deploy-version / changelog pattern:
 *
 *  1. Snapshot the last seen plan tier in localStorage.
 *  2. Poll `/api/auth/me` (server-fresh `getUser()`); when the tier differs,
 *     show a refresh banner.
 *  3. Before reloading, stamp the new plan into sessionStorage.
 *  4. After the reload, show a one-time confirmation (unless a gifted
 *     PRO/MAX notice is pending — `GiftProNotice` already celebrates that).
 */
export function PlanChangeNotice() {
  const t = useT();
  const { user, loading } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const [noticePlan, setNoticePlan] = useState<PlanTier>("free");
  const [refreshing, setRefreshing] = useState(false);
  const dismissedRef = useRef(false);
  const mountedRef = useRef(true);
  const pendingPlanRef = useRef<PlanTier>("free");
  // 2026-09-13 canónico animaciones: entrada/salida animate-in/out
  // (paridad CookieConsent — solo transform+opacity, 150ms).
  const { shouldRender: shouldRenderBanner, isLeaving: isLeavingBanner } =
    useAnimatedOpen(showBanner, 150);
  const { shouldRender: shouldRenderNotice, isLeaving: isLeavingNotice } =
    useAnimatedOpen(showNotice, 150);

  const checkForPlanChange = useCallback(async () => {
    if (loading || !user) return;
    if (typeof document !== "undefined" && document.hidden) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    // PERF-01 dedup: ráfagas de visibility + tick inicial colapsan en 1 fetch.
    // L-47 ventana 30s (paridad OnlineGuard): colapsa ticks espaciados.
    if (planCheckInflight) return planCheckInflight;
    if (Date.now() - planCheckLastMs < PLAN_CHECK_DEDUP_MS) return;
    planCheckLastMs = Date.now();
    planCheckInflight = (async () => {
      try {
        // RESUELTO 2026-09-13 [B-P2] no-store OK (poll techo 15m): /api/auth/me es server no-store, fuera de los 5 cacheables; detectar cambio de plan exige frescura |fuente src/app/api/auth/me/route.ts:18
        const res = await fetch("/api/auth/me", { cache: "no-store" });
        if (!res.ok || !mountedRef.current) return;
        const data = (await res.json()) as { plan?: string | null };
        const current = normalizePlan(data.plan);
        if (!current) return; // Unknown plan — never nag.

        const stored = readSnapshot();
        if (!stored) {
          // First visit: remember the tier and stay quiet.
          writeSnapshot(current);
          return;
        }

        if (stored.plan !== current && !dismissedRef.current) {
          pendingPlanRef.current = current;
          setShowBanner(true);
        }
      } catch {
        // Network blip — don't bother the user.
      } finally {
        planCheckInflight = null;
      }
    })();
    return planCheckInflight;
  }, [loading, user]);

  useEffect(() => {
    mountedRef.current = true;

    // After a successful refresh from the banner, confirm the new plan once
    // (mirrors the post-update changelog prompt). Defer setState so we don't
    // cascade-render inside the effect body (react-hooks lint).
    let noticeTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      const raw = sessionStorage.getItem(PLAN_CHANGED_NOTICE_KEY);
      if (raw) {
        sessionStorage.removeItem(PLAN_CHANGED_NOTICE_KEY);
        const parsed = JSON.parse(raw) as Partial<PlanState>;
        const plan =
          typeof parsed?.plan === "string" ? normalizePlan(parsed.plan) : null;
        if (plan) {
          noticeTimer = setTimeout(() => {
            void (async () => {
              if (!mountedRef.current) return;
              // GiftProNotice already celebrates gifted PRO/MAX in its own
              // modal — don't stack a second one on top of it.
              // 2026-09-19 perf-cpu (owner compartido): gift check via
              // fetchUsageShared con force (one-shot post-reload, frescura
              // justificada tras cambio de plan) — comparte inflight con
              // use-chat-quotas en vez de fetch propio con no-store.
              // 2026-09-28 SEED 2026-09-28-bae4824d #27: el force bypassea la
              // ventana 30s pero NO los guards hidden/offline (Free-tier CPU
              // budget: cero-wakeups en oculta, paridad checkForPlanChange +
              // StatusDashboard) — en tab oculta/offline se omite el fetch y
              // se muestra el aviso de plan sin gift-check (fail-open visual).
              let giftActive = false;
              try {
                // Oculta/offline: sin gift-check — se muestra el aviso de
                // plan igual (fail-open visual); el fetch vendrá en el
                // siguiente tick visible.
                const hidden =
                  typeof document !== "undefined" && document.hidden;
                const offline =
                  typeof navigator !== "undefined" && !navigator.onLine;
                if (!hidden && !offline) {
                  const data = await fetchUsageShared(true);
                  const gift = data?.giftNotice;
                  giftActive = Boolean(gift && gift.dismissed !== true);
                }
              } catch {
                // Unreadable usage state — still show the plan notice.
              }
              if (!giftActive && mountedRef.current) {
                setNoticePlan(plan);
                setShowNotice(true);
              }
            })();
          }, 0);
        }
      }
    } catch {
      // sessionStorage may be blocked; skip the confirmation.
    }

    const id = setTimeout(() => void checkForPlanChange(), 0);
    // 2026-09-19 perf-cpu: cadena de setTimeout con jitter 16-18min (no
    // setInterval fijo) + guards hidden/offline por tick + re-check en
    // visibilitychange. Un solo owner por tab.
    // 2026-09-21 cero-wakeups en oculta: la cadena se detiene en background
    // (clearTimeout) y se reanuda al volver — antes el timeout seguía vivo y
    // despertaba la tab solo para saltar el tick.
    let interval: ReturnType<typeof setTimeout> | undefined;
    let scheduled = false;
    const schedule = () => {
      if (scheduled || !mountedRef.current) return;
      scheduled = true;
      interval = setTimeout(() => {
        scheduled = false;
        void checkForPlanChange();
        if (
          mountedRef.current &&
          typeof document !== "undefined" &&
          !document.hidden
        )
          schedule();
      }, nextCheckDelayMs());
    };
    const stopChain = () => {
      if (interval) clearTimeout(interval);
      interval = undefined;
      scheduled = false;
    };
    schedule();
    // Lote Fase 5: re-chequear al volver a la pestaña (el intervalo no
    // dispara en background y el banner de plan quedaría rancio).
    const onVisible = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        stopChain();
        return;
      }
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      void checkForPlanChange();
      schedule();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      mountedRef.current = false;
      if (noticeTimer) clearTimeout(noticeTimer);
      clearTimeout(id);
      stopChain();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [checkForPlanChange]);

  const handleReload = useCallback(() => {
    setRefreshing(true);
    try {
      sessionStorage.setItem(
        PLAN_CHANGED_NOTICE_KEY,
        JSON.stringify({ plan: pendingPlanRef.current }),
      );
    } catch {
      // sessionStorage may be blocked; still reload.
    }
    writeSnapshot(pendingPlanRef.current);
    window.location.reload();
  }, []);

  const handleDismiss = useCallback(() => {
    dismissedRef.current = true;
    setShowBanner(false);
  }, []);

  const handleNoticeClose = useCallback(() => {
    setShowNotice(false);
  }, []);

  // 2026-09-13 a11y lote 2.1: foco inicial + trap Tab + Escape + retorno
  // al trigger (patrón chat-sidebar lightbox vía useFocusTrapDialog).
  // Solo el modal (el banner inferior no es modal: sin trap).
  const noticeDialogRef = useFocusTrapDialog(showNotice, handleNoticeClose);

  const planName = useCallback(
    (plan: PlanTier) =>
      // RESUELTO 2026-09-12 [B-P3] etiqueta Max+ (key planMaxplusName existe) |fuente src/lib/plans.ts:239
      plan === "maxplus"
        ? t("planMaxplus")
        : plan === "max"
          ? t("planMax")
          : plan === "pro"
            ? t("planPro")
            : plan === "go"
              ? t("planGo")
              : t("planFree"),
    [t],
  );

  if (shouldRenderNotice) {
    return (
      <div
        data-plan-change-notice
        className="vortixy-safe-area fixed inset-0 z-[9999] flex items-end justify-center p-4 sm:items-center"
        role="presentation"
      >
        <button
          type="button"
          className="absolute inset-0 bg-black/75"
          aria-label={t("planChangeOkay")}
          onClick={handleNoticeClose}
        />
        <div
          ref={noticeDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="plan-change-notice-title"
          style={{ background: "var(--background-elevated)" }}
          // 2026-09-16 fondo opaco: modal-surface (en dark .lovable-card es 75% + blur y el chat se transparentaba).
          className={`lovable-card lovable-card--static modal-surface relative w-full max-w-md border border-[var(--border-strong)] bg-[var(--background-elevated)] p-5 shadow-[var(--shadow-panel)] ${isLeavingNotice ? "animate-out fade-out zoom-out-95 duration-150 motion-reduce:animate-none" : "animate-in fade-in zoom-in-95 duration-150 motion-reduce:animate-none"}`}
        >
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 ring-1 ring-[var(--accent)]/25">
              <Sparkles
                className="size-5 text-[var(--accent)]"
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">
              <p
                id="plan-change-notice-title"
                className="text-base font-semibold text-foreground leading-snug"
              >
                {t("planChangeModalTitle").replace(
                  "{plan}",
                  planName(noticePlan),
                )}
              </p>
              <p className="text-sm text-foreground-soft leading-relaxed">
                {t("planChangeModalBody")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleNoticeClose}
              className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-[var(--muted)] hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              aria-label={t("planChangeOkay")}
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={handleNoticeClose}
              className="rounded-full bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--accent-foreground)] transition-[opacity,transform] hover:opacity-90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {t("planChangeOkay")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!shouldRenderBanner) return null;

  // 2026-09-25 FASE 3 FIX design-docs-auth: colisión con UpdateNotification
  // (ambos fixed bottom-20 z-[9999] se solapaban cuando plan-cambiado +
  // update coexisten) — stacking determinista: este aviso apila ARRIBA
  // (bottom-40 ≈160px) y el de update conserva bottom-20; mismo z, sin
  // cola de estado compartida.
  return (
    <div
      data-plan-change-notice
      className={`fixed bottom-40 left-1/2 z-[9999] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 ${isLeavingBanner ? "animate-out fade-out slide-out-to-bottom-1 duration-150 motion-reduce:animate-none" : "animate-in fade-in slide-in-from-bottom-1 duration-150 motion-reduce:animate-none"}`}
    >
      <div
        style={{ background: "var(--background-elevated)" }}
        className="lovable-card lovable-card--static border-[var(--border-strong)] bg-[var(--background-elevated)]"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 ring-1 ring-[var(--accent)]/25">
            <RefreshCw
              className="size-4 text-[var(--accent)]"
              aria-hidden="true"
            />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="text-sm font-semibold text-foreground">
              {t("planChangeBannerTitle")}
            </p>
            <p className="text-xs text-foreground-soft leading-relaxed">
              {t("planChangeBannerBody")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-[var(--muted)] hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            aria-label={t("updateAvailableDismiss")}
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        </div>
        <div className="border-t border-[var(--border)] px-4 py-2.5">
          <button
            type="button"
            onClick={handleReload}
            disabled={refreshing}
            className="w-full rounded-full bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--accent-foreground)] transition-[opacity,transform] hover:opacity-90 active:scale-[0.98] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
          >
            {refreshing
              ? t("updateAvailableUpdating")
              : t("updateAvailableButton")}
          </button>
        </div>
      </div>
    </div>
  );
}
